import SysRecord from "app/class/record";
import { PlatformThemeRecord } from "app/theme/platformTheme";

export type GateApiScope =
    | "auth.session"
    | "auth.login"
    | "auth.login.remember"
    | "auth.login.remembered"
    | "auth.login_redirect"
    | "auth.logout"
    | "router.navigate"
    | "ui.notify"
    | "record.query"
    | "record.get"
    | "record.create"
    | "record.update"
    | "record.delete"
    | "email.send"
    | "email.send_template"
    | "email.render_template"
    | "email.upload_attachment"
    | "email.delete_attachment"
    | "attachment.list"
    | "attachment.upload"
    | "attachment.delete"
    | "attachment.download_url"
    | "attachment.view_url"
    | "service_portal.submit"
    | "service_portal.form.get"
    | "service_portal.invite.get"
    | "service_portal.invite.submit"
    | "service_portal.reference.lookup"
    | "translations.coverage"
    | "service_portal.request.public_get"
    | "service_portal.attachment.list"
    | "service_portal.attachment.upload"
    | "service_portal.attachment.delete"
    | "service_portal.attachment.download_url"
    | "service_portal.attachment.view_url"
    | "session.user.create"
    | "session.user.update"
    | "session.user.password";

export interface GateWidgetDefinition {
    id?: string;
    code: string;
    name: string;
    description?: string;
    template?: string;
    script?: string;
    style?: string;
    propsSchema?: Record<string, unknown>;
    eventSchema?: Record<string, unknown>;
    apiScope?: GateApiScope[];
    active?: boolean;
    props?: Record<string, unknown>;
}

export interface GateWidgetReference {
    code: string;
    props?: Record<string, unknown>;
    name?: string;
}

export interface GateRouteDefinition {
    path: string;
    pageCode?: string;
    widgetCode?: string;
    props?: Record<string, unknown>;
    requiresAuth?: boolean;
}

export interface GateDefinition {
    id?: string;
    name: string;
    slug: string;
    title?: string;
    description?: string;
    active?: boolean;
    isPublic?: boolean;
    runtimeVersion?: string;
    template?: string;
    style?: string;
    script?: string;
    head?: string;
    meta?: Record<string, unknown>;
    components?: Record<string, string>;
    widgets?: GateWidgetDefinition[];
    widgetRefs?: GateWidgetReference[];
    routes?: GateRouteDefinition[];
    permissions?: Record<string, unknown>;
    apiScope?: GateApiScope[];
    rawRecord?: SysRecord;
}

export interface GateLocationSnapshot {
    origin: string;
    href: string;
    pathname: string;
    routePath: string;
    search: string;
    hash: string;
    params: Record<string, string>;
    query: Record<string, string | string[]>;
}

export interface GateNavigatePayload {
    to?: string;
    replace?: boolean;
    state?: unknown;
    scope?: "global" | "local";
}

export interface GateBridgeRequestMessage {
    source: "gate-runtime";
    type: "gate:invoke";
    requestId: string;
    method: string;
    payload?: unknown;
    widgetCode?: string;
}

export interface GateBridgeErrorPayload {
    message: string;
    code?: string;
    details?: unknown;
    status?: number;
}

export interface GateBridgeResponseMessage {
    source: "gate-host";
    type: "gate:resolve" | "gate:reject" | "gate:ready";
    requestId?: string;
    payload?: unknown;
    // A plain string for scope-denial rejections raised by PortalFrame itself;
    // the richer shape carries the backend's { code, message, details }
    // envelope through for API call failures (e.g. service_portal.submit),
    // so form onError handlers see the real validation reason instead of
    // axios' generic "Request failed with status code 422".
    error?: string | GateBridgeErrorPayload;
}

export interface GateBridgeLocationSyncMessage {
    source: "gate-host";
    type: "gate:location-sync";
    payload: GateLocationSnapshot;
}

export interface GateRuntimeModalPayload {
    data?: Record<string, unknown>;
}

export interface GateRuntimeCurrencyRule {
    id?: string;
    code: string;
    symbol: string;
    decimalPlaces: number;
}

export interface GateRuntimeCurrencyPayload {
    currencies: GateRuntimeCurrencyRule[];
    defaultCurrencyCode?: string | null;
    defaultNumberFormat?: string | null;
    userNumberFormat?: string | null;
}

export interface GateRuntimeSessionPayload {
    id?: string;
    name?: string;
    email?: string;
    preferredLanguage?: string | null;
    preferred_language?: string | null;
    numberFormat?: string | null;
    number_format?: string | null;
    dateTimeFormat?: string | null;
    date_time_format?: string | null;
    timeZone?: string | null;
    time_zone?: string | null;
    roles?: string[];
}

export interface GateRuntimeFeatures {
    formRuntime: boolean;
    currency: boolean;
    translations: boolean;
    tailwindJit: boolean;
}

export interface GateRuntimeContextSyncPayload {
    i18n?: GateRuntimeI18nPayload;
    theme?: PlatformThemeRecord;
    themeCssVariables?: Record<string, string>;
    currency?: GateRuntimeCurrencyPayload;
    session?: GateRuntimeSessionPayload | null;
}

export interface GateRuntimeContextSyncMessage {
    source: "gate-host";
    type: "gate:context-sync";
    payload: GateRuntimeContextSyncPayload;
}

export interface GateRuntimeBootstrapPayload {
    page: GateDefinition;
    allowedMethods: GateApiScope[];
    i18n: GateRuntimeI18nPayload;
    location: GateLocationSnapshot;
    vueRuntimeSource: string;
    theme?: PlatformThemeRecord;
    modal?: GateRuntimeModalPayload;
    currency?: GateRuntimeCurrencyPayload;
    session?: GateRuntimeSessionPayload | null;
    features?: GateRuntimeFeatures;
}

export interface GateRuntimeI18nPayload {
    locale: string;
    fallbackLocale?: string;
    messages: Record<string, unknown>;
    messagesByLocale?: Record<string, Record<string, unknown>>;
    supportedLocales?: string[];
    isAuthenticated?: boolean;
}

export const DEFAULT_GATE_RUNTIME_VERSION = "1.0.0";

const readString = (record: SysRecord, keys: string[]) => {
    const key = keys.find((candidate) => typeof record?.[candidate] === "string" && record[candidate]);
    return key ? String(record[key]) : "";
};

const readJson = <T>(record: SysRecord, keys: string[], fallback: T): T => {
    const key = keys.find((candidate) => record?.[candidate] !== undefined && record[candidate] !== null && record[candidate] !== "");

    if (!key) {
        return fallback;
    }

    const value = record[key];

    if (typeof value === "string") {
        try {
            return JSON.parse(value) as T;
        } catch (error) {
            console.warn(`Failed to parse Gate JSON field "${key}"`, error);
            return fallback;
        }
    }

    return value as T;
};

export const normalizeGateWidgetDefinition = (record: SysRecord): GateWidgetDefinition => ({
    id: record.id,
    code: readString(record, ["code", "name"]),
    name: readString(record, ["name", "code"]) || "gate-widget",
    description: readString(record, ["description"]),
    template: readString(record, ["template_code", "template"]),
    style: readString(record, ["style_code", "style", "css"]),
    script: readString(record, ["script_code", "script"]),
    propsSchema: readJson<Record<string, unknown>>(record, ["props_schema_json", "props_schema"], {}),
    eventSchema: readJson<Record<string, unknown>>(record, ["event_schema_json", "event_schema"], {}),
    apiScope: readJson<GateApiScope[]>(record, ["api_scope_json", "api_scope"], []),
    active: Boolean(record.active ?? true),
});

export const normalizeGateWidgetReferences = (input: unknown): GateWidgetReference[] => {
    if (!Array.isArray(input)) {
        return [];
    }

    return input
        .map((item) => {
            if (typeof item === "string") {
                return { code: item };
            }

            if (item && typeof item === "object" && "code" in item && typeof item.code === "string") {
                return {
                    code: item.code,
                    props: typeof item.props === "object" && item.props !== null ? item.props as Record<string, unknown> : undefined,
                    name: typeof item.name === "string" ? item.name : undefined,
                };
            }

            return null;
        })
        .filter((item): item is GateWidgetReference => Boolean(item?.code));
};

export const mergeGateWidgets = (
    widgetRefs: GateWidgetReference[],
    resolvedWidgets: GateWidgetDefinition[]
) => {
    if (widgetRefs.length === 0) {
        return resolvedWidgets;
    }

    const widgetMap = new Map(resolvedWidgets.map((widget) => [widget.code, widget]));

    return widgetRefs.map((ref) => {
        const resolved = widgetMap.get(ref.code);

        if (!resolved) {
            return {
                code: ref.code,
                name: ref.name || ref.code,
                props: ref.props,
                active: false,
            };
        }

        return {
            ...resolved,
            name: ref.name || resolved.name,
            props: ref.props,
        };
    });
};

export const normalizeGateDefinition = (record: SysRecord): GateDefinition => {
    const slug = readString(record, ["slug", "name"]);
    const script = readString(record, ["script_code", "script"]);
    const style = readString(record, ["style_code", "style", "css"]);
    const template = readString(record, ["template_code", "template", "html"]);
    const widgetRefs = normalizeGateWidgetReferences(readJson<unknown[]>(record, ["widget_tree_json", "widgets"], []));

    return {
        id: record.id,
        name: readString(record, ["name", "slug"]) || "gate-page",
        slug: slug || "gate-page",
        title: readString(record, ["title", "label", "name"]),
        description: readString(record, ["description"]),
        active: Boolean(record.active ?? true),
        isPublic: Boolean(record.public ?? false),
        runtimeVersion: readString(record, ["runtime_version"]) || DEFAULT_GATE_RUNTIME_VERSION,
        template,
        style,
        script,
        head: readString(record, ["head_code", "head"]),
        meta: readJson<Record<string, unknown>>(record, ["meta_json", "meta_code", "meta"], {}),
        components: readJson<Record<string, string>>(record, ["component_registry_json", "components"], {}),
        widgets: [],
        widgetRefs,
        routes: readJson<GateRouteDefinition[]>(record, ["route_tree_json", "routes"], []),
        permissions: readJson<Record<string, unknown>>(record, ["permission_json", "permissions"], {}),
        apiScope: readJson<GateApiScope[]>(record, ["api_scope_json", "api_scope"], []),
        rawRecord: record,
    };
};
